import axios from 'axios'
import { API_URL, getAuthHeader } from '@/services/api.service.js'
import { decompressJson } from '@/utils/Utils.js'
import { defaultOption } from '@/lib/OrgChartFilters'
import { logger } from '@/utils/logger'

const isCanceledError = (error) =>
  axios.isCancel?.(error) ||
  error?.code === 'ERR_CANCELED' ||
  error?.name === 'CanceledError' ||
  error?.name === 'AbortError'

/**
 * Fetches organizational map data for a specific board.
 * @param {Object} params - The parameters for the API request, including boardId.
 * @param {AbortSignal} [params.signal] - Optional abort signal to cancel the request.
 * @returns {Promise<Object|null>} - A promise that resolves with the API response or null if an error occurs.
 */
export const fetchOrgmap = async ({ boardId, filters, category, subCategory, signal }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/hub/orgmap/${boardId}`,
      { data: { category, subCategory, filters } },
      {
        headers: { ...(await getAuthHeader()) },
        signal
      }
    )
    if (ret.status === 200) {
      const data = decompressJson(ret.data || {})
      return data
    } else throw new Error(ret.statusText)
  } catch (error) {
    if (isCanceledError(error)) {
      throw error
    }
    logger.info(error)
    return null
  }
}

/**
 * Fetches people list for a specific span.
 * @param {Object} params - The parameters for the API request, including boardId.
 * @returns {Promise<Object|null>} - A promise that resolves with the API response or null if an error occurs.
 */
export const getSpanPeople = async ({
  boardId,
  category,
  filters,
  from,
  to,
  value,
  queryPlanData
}) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/hub/span-of-control/${boardId}/getSpanPeople`,
      { data: { category, filters, from, to, value, queryPlanData } },
      { headers: { ...(await getAuthHeader()) } }
    )
    if (ret.status === 200) {
      const data = decompressJson(ret.data || {})
      return data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error)
    return null
  }
}

/**
 * Fetches layer and depth data for a specific board.
 * @param {Object} params - The parameters for the API request, including boardId.
 * @returns {Promise<Object|null>} - A promise that resolves with the API response or null if an error occurs.
 */
export const fetchLayerAndDepth = async ({ boardId, filters, category, subCategory, signal }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/hub/layers/${boardId}`,
      { data: { category, filters, subCategory } },
      {
        headers: {
          ...(await getAuthHeader())
        },
        signal
      }
    )
    if (ret.status === 200) {
      const data = decompressJson(ret.data || {})
      return data
    } else throw new Error(ret.statusText)
  } catch (error) {
    if (isCanceledError(error)) {
      throw error
    }
    logger.info(error)
    return null
  }
}

/**
 * Fetches layer and span of control distribution data for a specific board.
 * @param {Object} params - The parameters for the API request, including boardId.
 * @returns {Promise<Object|null>} - A promise that resolves with the API response or null if an error occurs.
 */
export const fetchLayerAndSpanOfControl = async ({ boardId, filters, category, signal }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/hub/layers-span-of-control/${boardId}`,
      { data: { category, filters } },
      {
        headers: {
          ...(await getAuthHeader())
        },
        signal
      }
    )
    if (ret.status === 200) {
      const data = decompressJson(ret.data || {})
      return data
    } else throw new Error(ret.statusText)
  } catch (error) {
    if (isCanceledError(error)) {
      throw error
    }
    logger.info(error)
    return null
  }
}

/**
 * Fetches distribution data for a specific board.
 * @param {Object} params - The parameters for the API request, including boardId.
 * @returns {Promise<Object|null>} - A promise that resolves with the API response or null if an error occurs.
 */
export const fetchDistribution = async ({ boardId, filters, category, signal }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/hub/distribution/${boardId}`,
      { data: { category, filters } },
      {
        headers: {
          ...(await getAuthHeader())
        },
        signal
      }
    )
    if (ret.status === 200) {
      const data = decompressJson(ret.data || {})
      return data
    } else throw new Error(ret.statusText)
  } catch (error) {
    if (isCanceledError(error)) {
      throw error
    }
    logger.info(error)
    return null
  }
}

/**
 * Fetches cost distribution data for a specific board.
 * @param {Object} params - The parameters for the API request, including boardId.
 * @returns {Promise<Object|null>} - A promise that resolves with the API response or null if an error occurs.
 */
export const fetchCostDistribution = async ({ boardId, filters, category, isAverages, signal }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/hub/cost-distribution/${boardId}`,
      { data: { category, filters, isAverages } },
      {
        headers: {
          ...(await getAuthHeader())
        },
        signal
      }
    )
    if (ret.status === 200) {
      const data = decompressJson(ret.data || {})
      return data
    } else throw new Error(ret.statusText)
  } catch (error) {
    if (isCanceledError(error)) {
      throw error
    }
    logger.info(error)
    return null
  }
}

/**
 * Fetches growth distribution data for a specific board.
 * @param {Object} params - The parameters for the API request, including boardId.
 * @returns {Promise<Object|null>} - A promise that resolves with the API response or null if an error occurs.
 */
export const fetchGrowthDistribution = async ({ boardId, filters, startDate, endDate, signal }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/hub/growth-distribution/${boardId}`,
      { data: { filters, startDate, endDate } },
      {
        headers: {
          ...(await getAuthHeader())
        },
        signal
      }
    )
    if (ret.status === 200) {
      const data = decompressJson(ret.data || {})
      return data
    } else throw new Error(ret.statusText)
  } catch (error) {
    if (isCanceledError(error)) {
      throw error
    }
    logger.info(error)
    return null
  }
}

/**
 * Fetches growth distribution dates for a specific board.
 * @param {Object} params - The parameters for the API request - boardId and filters.
 * @returns {Promise<Object|null>} - A promise that resolves with the API response or null if an error occurs.
 */
export const fetchGrowthDistributionDates = async ({ boardId, filters, signal }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/hub/growth-distribution/${boardId}/dates`,
      { data: { filters } },
      {
        headers: {
          ...(await getAuthHeader())
        },
        signal
      }
    )
    if (ret.status === 200) {
      return ret.data || {}
    } else if (ret.status === 400) {
      return {}
    }
    throw new Error(ret.statusText)
  } catch (error) {
    if (isCanceledError(error)) {
      throw error
    }
    logger.info(error)
    return null
  }
}

/**
 * Fetches pyramid chart data for a specific board.
 * @param {Object} params - The parameters for the API request, including boardId.
 * @returns {Promise<Object|null>} - A promise that resolves with the API response or null if an error occurs.
 */
export const fetchPyramidChart = async ({ boardId, filters, category, showBy, signal }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/hub/pyramid/${boardId}`,
      { data: { filters, showBy, category } },
      {
        headers: {
          ...(await getAuthHeader())
        },
        signal
      }
    )
    if (ret.status === 200) {
      const data = decompressJson(ret.data || {})
      return data
    } else throw new Error(ret.statusText)
  } catch (error) {
    if (isCanceledError(error)) {
      throw error
    }
    logger.info(error)
    return null
  }
}

/**
 * Fetches pyramid chart data for a specific board.
 * @param {Object} params - The parameters for the API request, including boardId.
 * @returns {Promise<Object|null>} - A promise that resolves with the API response or null if an error occurs.
 */
export const updateOrder = async ({ boardId, category, order, hub }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/hub/${boardId}/updateOrder/${hub}`,
      { data: { category, order } },
      {
        headers: {
          ...(await getAuthHeader())
        }
      }
    )
    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error)
    return null
  }
}

/**
 * Fetches span of control data for a specific board.
 * @param {Object} params - The parameters for the API request, including boardId.
 * @returns {Promise<Object|null>} - A promise that resolves with the API response or null if an error occurs.
 */
export const fetchSpanOfControl = async ({ boardId, filters, category, signal }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/hub/span-of-control/${boardId}`,
      { data: { category, filters } },
      {
        headers: {
          ...(await getAuthHeader())
        },
        signal
      }
    )
    if (ret.status === 200) {
      const data = decompressJson(ret.data || {})
      return data
    } else throw new Error(ret.statusText)
  } catch (error) {
    if (isCanceledError(error)) {
      throw error
    }
    logger.info(error)
    return null
  }
}

export const fetchAverageSpanOfControl = async ({ boardId, filters, category, signal }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/hub/average-span-of-control/${boardId}`,
      { data: { category, filters } },
      {
        headers: {
          ...(await getAuthHeader())
        },
        signal
      }
    )
    if (ret.status === 200) {
      const data = decompressJson(ret.data || {})
      return data
    } else throw new Error(ret.statusText)
  } catch (error) {
    if (isCanceledError(error)) {
      throw error
    }
    logger.info(error)
    return null
  }
}

/**
 * Fetches icicle chart data for a specific board.
 * @param {Object} params - The parameters for the API request, including boardId.
 * @returns {Promise<Object|null>} - A promise that resolves with the API response or null if an error occurs.
 */
export const fetchIcicle = async ({ boardId, filters, showById, signal }) => {
  // The icicle endpoint reads an empty filter list as "no people", not
  // "everyone" (its show-all fast path matches the explicit filter by name),
  // and it feeds both the Icicle and Sunburst charts. Unfiltered callers
  // (AI chat embeds, slide-pack slides without filters) mean "everyone", so
  // send the same default the hub pages send.
  const effectiveFilters = filters?.length ? filters : [defaultOption]
  try {
    const ret = await axios.post(
      `${await API_URL()}/hub/icicle/${boardId}`,
      { data: { filters: effectiveFilters, showById } },
      {
        headers: {
          ...(await getAuthHeader())
        },
        signal
      }
    )
    if (ret.status === 200) {
      if (ret.data) {
        return decompressJson(ret.data)
      }
      return {}
    } else throw new Error(ret.statusText)
  } catch (error) {
    if (isCanceledError(error)) {
      throw error
    }
    logger.info(error)
    return null
  }
}

/**
 * Fetches stacked headcount cost data for a specific board.
 * @param {Object} params - The parameters for the API request, including boardId.
 * @returns {Promise<Object|null>} - A promise that resolves with the API response or null if an error occurs.
 */
export const fetchStackedHeadcountCost = async ({
  boardId,
  filters,
  category,
  subCategories,
  signal
}) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/hub/stacked-headcount-cost/${boardId}`,
      { data: { category, subCategories, filters } },
      {
        headers: {
          ...(await getAuthHeader())
        },
        signal
      }
    )
    if (ret.status === 200) {
      const data = decompressJson(ret.data || {})
      return data
    } else throw new Error(ret.statusText)
  } catch (error) {
    if (isCanceledError(error)) {
      throw error
    }
    logger.info(error)
    return null
  }
}

/**
 * Fetches skills heatmap data for a specific board.
 * @param {Object} params - The parameters for the API request, including boardId.
 * @returns {Promise<Object|null>} - A promise that resolves with the API response or null if an error occurs.
 */
export const fetchSkillsHeatmap = async ({ boardId, filters, category, subCategories, signal }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/hub/skills-heatmap/${boardId}`,
      { data: { category, subCategories, filters } },
      {
        headers: {
          ...(await getAuthHeader())
        },
        signal
      }
    )
    if (ret.status === 200) {
      const data = decompressJson(ret.data || {})
      return data
    } else throw new Error(ret.statusText)
  } catch (error) {
    if (isCanceledError(error)) {
      throw error
    }
    logger.info(error)
    return null
  }
}

/**
 * Fetches layers table for a specific board.
 * @param {Object} params - The parameters for the API request, including boardId.
 * @returns {Promise<Object|null>} - A promise that resolves with the API response or null if an error occurs.
 */
export const fetchLayersTable = async ({ boardId, filters, signal }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/hub/layers-table/${boardId}`,
      { data: { filters } },
      {
        headers: {
          ...(await getAuthHeader())
        },
        signal
      }
    )
    if (ret.status === 200) {
      const data = decompressJson(ret.data || {})
      return data
    } else throw new Error(ret.statusText)
  } catch (error) {
    if (isCanceledError(error)) {
      throw error
    }
    logger.info(error)
    return null
  }
}

/**
 * Fetches people in a specific headcount distribution category
 * @param {Object} params - Parameters for the request
 * @returns {Promise<Array>} - List of people in the category
 */
export const getHeadcountPeople = async ({
  value,
  boardId,
  filters = [],
  category,
  seriesName
}) => {
  try {
    const response = await axios.post(
      `${await API_URL()}/hub/distribution/${boardId}/people`,
      {
        data: {
          value,
          filters,
          category,
          seriesName
        }
      },
      {
        headers: {
          ...(await getAuthHeader())
        }
      }
    )
    return response.data
  } catch (error) {
    logger.error('Error fetching headcount people:', error.message)
    throw error
  }
}

/**
 * Fetches people in a specific headcount cost distribution category
 * @param {Object} params - Parameters for the request
 * @returns {Promise<Array>} - List of people in the category
 */
export const getHeadcountCostPeople = async ({
  value,
  boardId,
  filters = [],
  category,
  seriesName,
  isAverages = false
}) => {
  try {
    const response = await axios.post(
      `${await API_URL()}/hub/cost-distribution/${boardId}/people`,
      {
        data: {
          value,
          filters,
          category,
          seriesName,
          isAverages
        }
      },
      {
        headers: {
          ...(await getAuthHeader())
        }
      }
    )
    return response.data
  } catch (error) {
    logger.error('Error fetching headcount cost people:', error.message)
    throw error
  }
}

/**
 * Fetches people list for a specific cell in the Layer and Depth chart.
 * @param {Object} params - The parameters for the API request.
 * @param {string} params.rowCategory - The row category value (e.g., "Level 3").
 * @param {string} params.columnCategory - The column category value (e.g., "Engineering").
 * @param {string} params.boardId - The board ID.
 * @param {Array} params.filters - Applied filters.
 * @param {string} params.rowCategoryId - The ID of the row category field.
 * @param {string} params.columnCategoryId - The ID of the column category field.
 * @returns {Promise<Array>} - A promise that resolves with the list of people.
 */
export const getLayerAndDepthPeople = async ({
  rowCategory,
  columnCategory,
  boardId,
  filters = [],
  rowCategoryId,
  columnCategoryId,
  isAfter
}) => {
  try {
    const response = await axios.post(
      `${await API_URL()}/hub/layers/${boardId}/people`,
      {
        data: {
          rowCategory,
          columnCategory,
          filters,
          rowCategoryId,
          columnCategoryId,
          isAfter
        }
      },
      {
        headers: {
          ...(await getAuthHeader())
        }
      }
    )

    return response.data
  } catch (error) {
    logger.error('Error fetching layer and depth people:', error.message)
    throw error
  }
}

/**
 * Fetches people in a specific pyramid chart category
 * @param {Object} params - Parameters for the request
 * @returns {Promise<Array>} - List of people in the category
 */
export const getPyramidChartPeople = async ({
  value,
  boardId,
  filters = [],
  category,
  showBy,
  seriesName
}) => {
  try {
    const response = await axios.post(
      `${await API_URL()}/hub/pyramid/${boardId}/people`,
      {
        data: {
          value,
          filters,
          category,
          showBy,
          seriesName
        }
      },
      {
        headers: {
          ...(await getAuthHeader())
        }
      }
    )
    return response.data
  } catch (error) {
    logger.error('Error fetching pyramid chart people:', error.message)
    throw error
  }
}

/**
 * Fetches people list for a specific cell in the Layer and Span of Control chart.
 * @param {Object} params - The parameters for the API request.
 * @param {string} params.layerCategory - The layer category value (e.g., "1", "2", "3").
 * @param {string} params.spanCategory - The span of control category value (e.g., "0", "1-3", "≥10").
 * @param {string} params.boardId - The board ID.
 * @param {Array} params.filters - Applied filters.
 * @param {string} params.isAfter - Whether to fetch people for the "After" series.
 * @returns {Promise<Array>} - A promise that resolves with the list of people.
 */
export const getLayerAndSpanOfControlPeople = async ({
  layerCategory,
  spanCategory,
  boardId,
  filters = [],
  isAfter
}) => {
  try {
    const response = await axios.post(
      `${await API_URL()}/hub/layers-span-of-control/${boardId}/people`,
      {
        data: {
          layerCategory,
          spanCategory,
          filters,
          isAfter
        }
      },
      {
        headers: {
          ...(await getAuthHeader())
        }
      }
    )

    return response.data
  } catch (error) {
    logger.error('Error fetching layer and span of control people:', error.message)
    throw error
  }
}
/**
 * Fetches people list for a specific category in the Average Span of Control chart.
 * @param {Object} params - The parameters for the API request.
 * @param {string} params.value - The category value (e.g., "Engineering", "Marketing").
 * @param {string} params.boardId - The board ID.
 * @param {Array} params.filters - Applied filters.
 * @param {string} params.category - The category field ID.
 * @param {string} params.seriesName - The series name (for plan comparison).
 * @returns {Promise<Array>} - A promise that resolves with the list of people.
 */
export const getAverageSpanOfControlPeople = async ({
  value,
  boardId,
  filters = [],
  category,
  seriesName
}) => {
  try {
    const response = await axios.post(
      `${await API_URL()}/hub/average-span-of-control/${boardId}/people`,
      {
        data: {
          value,
          filters,
          category,
          seriesName
        }
      },
      {
        headers: {
          ...(await getAuthHeader())
        }
      }
    )

    return response.data
  } catch (error) {
    logger.error('Error fetching average span of control people:', error.message)
    throw error
  }
}

/**
 * Fetches people list for a specific cell in the OrgMap chart.
 * @param {Object} params - The parameters for the API request.
 * @param {string} params.categoryValue - The value of the primary category (e.g., "Toronto").
 * @param {string} params.subcategoryValue - The value of the subcategory (e.g., "Finance").
 * @param {string} params.boardId - The board ID.
 * @param {Array} params.filters - Applied filters.
 * @param {string} params.category - The primary category field ID.
 * @param {string} params.subCategory - The subcategory field ID.
 * @returns {Promise<Array>} - A promise that resolves with the list of people.
 */
export const getOrgMapPeople = async ({
  categoryValue,
  subcategoryValue,
  boardId,
  filters = [],
  category,
  subCategory
}) => {
  try {
    const response = await axios.post(
      `${await API_URL()}/hub/orgmap/${boardId}/people`,
      {
        data: {
          categoryValue,
          subcategoryValue,
          filters,
          category,
          subCategory
        }
      },
      {
        headers: {
          ...(await getAuthHeader())
        }
      }
    )
    return response.data
  } catch (error) {
    logger.error('Error fetching orgmap people:', error.message)
    throw error
  }
}

/**
 * Fetches people in a specific stacked headcount cost category
 * @param {Object} params - Parameters for the request
 * @param {string} params.value - The category value (e.g., "Engineering", "Marketing")
 * @param {string} params.boardId - The board ID
 * @param {Array} params.filters - Applied filters
 * @param {string} params.category - The category field ID
 * @param {string} params.seriesName - The series name (for plan comparison)
 * @param {Object} params.subCategory - The subcategory information (id and value)
 * @returns {Promise<Array>} - List of people in the category
 */
export const getStackedHeadcountCostPeople = async ({
  value,
  boardId,
  filters = [],
  category,
  seriesName
}) => {
  try {
    const response = await axios.post(
      `${await API_URL()}/hub/stacked-headcount-cost/${boardId}/people`,
      {
        data: {
          value,
          filters,
          category,
          seriesName
        }
      },
      {
        headers: {
          ...(await getAuthHeader())
        }
      }
    )
    return response.data
  } catch (error) {
    logger.error('Error fetching stacked headcount cost people:', error.message)
    throw error
  }
}
